/**
 * DetailPageTabs — config-driven, router-agnosztikus tab-foundation (17-oldal roadmap W0 2. fele).
 *
 * Két használati mód:
 *  1. **Config-driven** `<DetailPageTabs tabs={[{key,label,content}]} />` — kis panelekhez (Worksheet-
 *     stílus: a panel-tartalom card-referencia). A nav + panelek egyben.
 *  2. **Compound** `<DetailTabsNav tabs={[{key,label}]} … /> + <DetailTabPanel tabKey=… activeKey=… >`
 *     — NAGY, helyben-maradó panelekhez (User/Inventory 200-400 soros panelek): a panel-JSX nem mozdul,
 *     csak wrapper-csere → minimal-churn migráció.
 *
 * Mindkettő teljes WAI-ARIA tabs-szemantika (tablist/tab/tabpanel + aria-selected + aria-controls/
 * aria-labelledby + roving tabindex + nyíl-nav). URL-sync NEM itt él (a @kgc/ui router-agnosztikus) —
 * a fogyasztó a `value`/`onValueChange` párba köti az app-szintű `useTabUrlSync` hookot.
 */
import { useCallback, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { cn } from '../lib/utils';

export interface DetailTabHeader {
  /** stabil kulcs (URL-érték + state-azonosító) */
  key: string;
  label: ReactNode;
  /** data-testid/id suffix; default a `key` */
  testId?: string;
}

export interface DetailTabDef extends DetailTabHeader {
  content: ReactNode;
}

// ─────────────────────────────────────────────────────────────────────────────
// Compound: DetailTabsNav (csak a tablist + tab-gombok, keyboard-nav)
// ─────────────────────────────────────────────────────────────────────────────
export interface DetailTabsNavProps {
  tabs: DetailTabHeader[];
  value: string;
  onValueChange: (key: string) => void;
  idPrefix?: string;
  ariaLabel?: string;
  className?: string;
}

export function DetailTabsNav({
  tabs,
  value,
  onValueChange,
  idPrefix = 'detail',
  ariaLabel,
  className,
}: DetailTabsNavProps) {
  const tablistRef = useRef<HTMLDivElement>(null);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      const currentIndex = tabs.findIndex(tb => tb.key === value);
      if (currentIndex === -1) return;
      let nextIndex: number | null = null;
      switch (e.key) {
        case 'ArrowRight':
          nextIndex = (currentIndex + 1) % tabs.length;
          break;
        case 'ArrowLeft':
          nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }
      e.preventDefault();
      const next = tabs[nextIndex];
      if (!next) return;
      onValueChange(next.key);
      const tid = next.testId ?? next.key;
      tablistRef.current
        ?.querySelector<HTMLButtonElement>(`[data-testid="${idPrefix}-tab-${tid}"]`)
        ?.focus();
    },
    [tabs, value, onValueChange, idPrefix]
  );

  return (
    <nav
      ref={tablistRef}
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'mb-6 flex overflow-x-auto border-b border-gray-200 dark:border-gray-700',
        className
      )}
    >
      {tabs.map(tab => {
        const selected = value === tab.key;
        const tid = tab.testId ?? tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${tid}`}
            aria-selected={selected}
            aria-controls={`${idPrefix}-tabpanel-${tid}`}
            data-testid={`${idPrefix}-tab-${tid}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onValueChange(tab.key)}
            onKeyDown={onKeyDown}
            className={cn(
              'whitespace-nowrap px-4 py-2 text-sm font-medium transition-colors focus:outline-none',
              selected
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-gray-700 dark:hover:text-gray-300'
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </nav>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Compound: DetailTabPanel (egy helyben-maradó panel — nagy JSX-blokkokhoz)
// ─────────────────────────────────────────────────────────────────────────────
export interface DetailTabPanelProps {
  /** ennek a panelnek a kulcsa */
  tabKey: string;
  /** az aktuálisan aktív kulcs */
  activeKey: string;
  idPrefix?: string;
  testId?: string;
  className?: string;
  children: ReactNode;
}

export function DetailTabPanel({
  tabKey,
  activeKey,
  idPrefix = 'detail',
  testId,
  className,
  children,
}: DetailTabPanelProps) {
  const tid = testId ?? tabKey;
  return (
    <div
      role="tabpanel"
      id={`${idPrefix}-tabpanel-${tid}`}
      aria-labelledby={`${idPrefix}-tab-${tid}`}
      data-testid={`${idPrefix}-tabpanel-${tid}`}
      hidden={activeKey !== tabKey}
      className={className}
    >
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Config-driven: DetailPageTabs (Nav + Panelek egyben, kis panelekhez)
// ─────────────────────────────────────────────────────────────────────────────
export interface DetailPageTabsProps {
  tabs: DetailTabDef[];
  /** controlled aktív kulcs — ha megadott, a komponens nem self-switch-el (a szülő dönt) */
  value?: string;
  /** uncontrolled kezdeti kulcs (default az első tab) */
  defaultValue?: string;
  onValueChange?: (key: string) => void;
  idPrefix?: string;
  ariaLabel?: string;
  className?: string;
}

export function DetailPageTabs({
  tabs,
  value,
  defaultValue,
  onValueChange,
  idPrefix = 'detail',
  ariaLabel,
  className,
}: DetailPageTabsProps) {
  const reactId = useId();
  const prefix = idPrefix || `detail-${reactId}`;

  const [internal, setInternal] = useState<string>(defaultValue ?? tabs[0]?.key ?? '');
  const isControlled = value !== undefined;
  const active = isControlled ? value : internal;

  const select = useCallback(
    (key: string) => {
      if (!isControlled) setInternal(key);
      onValueChange?.(key);
    },
    [isControlled, onValueChange]
  );

  return (
    <div className={cn('w-full', className)}>
      <DetailTabsNav
        tabs={tabs}
        value={active}
        onValueChange={select}
        idPrefix={prefix}
        {...(ariaLabel !== undefined ? { ariaLabel } : {})}
      />
      {tabs.map(tab => (
        <DetailTabPanel
          key={tab.key}
          tabKey={tab.key}
          activeKey={active}
          idPrefix={prefix}
          {...(tab.testId ? { testId: tab.testId } : {})}
        >
          {tab.content}
        </DetailTabPanel>
      ))}
    </div>
  );
}
